import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";

import { RegistryItem } from "../types";

import {
  applyEntityPreviewAsset,
  entityPreviewQueryKey,
  lookupEntityPreviewAsset,
  mergeInitialPreviewState,
} from "./entityPreviewCache";

import { useAuth } from "@/context/auth-context";
import { EntityPreviewAsset } from "@/queries/folder-entities";

interface UseEntityPreviewAssetResult {
  preview: EntityPreviewAsset | undefined;
  refreshPreview: () => Promise<EntityPreviewAsset | undefined>;
}

export const useEntityPreviewAsset = ({
  item,
  workspaceId,
}: {
  item: RegistryItem;
  workspaceId: number | null | undefined;
}): UseEntityPreviewAssetResult => {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () =>
      workspaceId === null || workspaceId === undefined
        ? null
        : entityPreviewQueryKey(workspaceId, item.type, item.id),
    [item.id, item.type, workspaceId],
  );

  const { data: cachedPreview } = useQuery<EntityPreviewAsset | undefined>({
    queryKey: queryKey ?? ["entity-preview", "unscoped", item.type, String(item.id)],
    enabled: false,
    initialData: item.preview,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!queryKey) {
      return;
    }

    queryClient.setQueryData<EntityPreviewAsset | undefined>(queryKey, currentPreview =>
      mergeInitialPreviewState(currentPreview, item.preview),
    );
  }, [item.preview, queryClient, queryKey]);

  const refreshPreview = useCallback(async () => {
    if (workspaceId === null || workspaceId === undefined || !auth?.userToken) {
      return undefined;
    }

    const preview = await lookupEntityPreviewAsset({
      workspaceId,
      entityType: item.type,
      entityId: String(item.id),
      userToken: auth.userToken,
      requestMissing: true,
    });

    if (preview) {
      applyEntityPreviewAsset(queryClient, {
        workspaceId,
        entityType: item.type,
        entityId: String(item.id),
        preview,
      });
    }

    return preview;
  }, [auth?.userToken, item.id, item.type, queryClient, workspaceId]);

  return {
    preview: mergeInitialPreviewState(cachedPreview, item.preview),
    refreshPreview,
  };
};
